import { type ComponentPropsWithoutRef, type ComponentRef, type Ref } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

const Root: typeof SelectPrimitive.Root = SelectPrimitive.Root

const Group: typeof SelectPrimitive.Group = SelectPrimitive.Group

const Value: typeof SelectPrimitive.Value = SelectPrimitive.Value

const triggerClassName = cn(
  'tw:flex tw:h-full tw:w-full tw:items-center tw:justify-between tw:py-2 tw:px-4',
  'tw:bg-white tw:border tw:rounded-lg tw:border-gray-400 tw:ring-primary-100 tw:shadow-xs',
  'tw:text-base tw:text-left tw:[&>span]:line-clamp-1',
  'tw:focus:outline-hidden tw:focus:border-primary-400 tw:focus:ring-4',
  'tw:data-placeholder:text-gray-500',
  'tw:aria-[invalid=true]:border-red-400 tw:aria-[invalid=true]:focus:border-red-500 tw:aria-[invalid=true]:focus:ring-red-200',
  'tw:disabled:cursor-not-allowed tw:disabled:text-gray-500 tw:disabled:bg-gray-100'
)

const TriggerChevron = () => (
  <Icon type="chevron-down" width={16} height={16} aria-hidden className="tw:shrink-0 tw:text-gray-400" />
)

const Trigger = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Trigger>>
}) => (
  <SelectPrimitive.Trigger ref={ref} className={cn(triggerClassName, className)} {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <TriggerChevron />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)

const Content = ({
  className,
  children,
  position = 'popper',
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Content>>
}) => (
  <SelectPrimitive.Portal>
    <div className="tailwind">
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'tw:bg-white tw:relative tw:z-50 tw:max-h-96 tw:min-w-[8rem]',
          'tw:overflow-hidden tw:rounded-md tw:border tw:border-gray-200 tw:shadow-md',
          'tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0 tw:data-[state=open]:zoom-in-95',
          'tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=closed]:zoom-out-95',
          'tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'tw:data-[side=bottom]:translate-y-1 tw:data-[side=left]:-translate-x-1 tw:data-[side=right]:translate-x-1 tw:data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'tw:p-1',
            position === 'popper' &&
              'tw:h-[var(--radix-select-trigger-height)] tw:w-full tw:min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </div>
  </SelectPrimitive.Portal>
)

const Label = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Label>>
}) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('tw:sticky tw:-top-1 tw:bg-white tw:px-2 tw:py-1.5 tw:text-base tw:font-semibold', className)}
    {...props}
  />
)

const Item = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Item>>
}) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:w-full tw:cursor-default tw:select-none tw:items-center tw:rounded-xs tw:py-1.5 tw:pl-2 tw:pr-8 tw:text-base tw:outline-hidden tw:focus:bg-primary-100 tw:focus:text-primary-900 tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="tw:absolute tw:right-2 tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center">
      <SelectPrimitive.ItemIndicator>
        <Icon type="check" width={16} height={16} />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)

const SelectSeparator = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
  ref?: Ref<ComponentRef<typeof SelectPrimitive.Separator>>
}) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-gray-200', className)}
    {...props}
  />
)

export { Root, Group, Value, Trigger, TriggerChevron, Content, Label, Item, SelectSeparator, triggerClassName }
